/*eslint max-lines-per-function: ["error", 421]*/
/*eslint complexity: ["error", 31]*/
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';

import { useAuth } from '../../../common/hooks/useAuth';
import { useSzafir } from '../../../common/hooks/useSzafir';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { SimP1StatusEnum } from '../../../common/spdCore/validation/enums';
import {
  GetExamDto,
  GetJudgmentWithSignedInfoDto,
  GetJudgmentWithSignedInfoDtoStatusP1Enum,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  judgmentData,
  validate,
} from '../../../common/spdCore/contexts/JudgmentContext';
import {
  CardType,
  CardTypeText,
} from '../../../common/spdCore/validation/schemas';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { mustBeSentToP1 } from '../Judgments/mustBeSentToP1';
import { sendJudgmentToP1 } from '../Szafir/sendJudgmentToP1';
import { signJudgmentPdfWithSzafir } from '../Szafir/signJudgmentPdfWithSzafir';

interface BtnProps {
  judgment: Partial<GetJudgmentWithSignedInfoDto>;
  exam: GetExamDto;
  onSave: (judg: Partial<GetJudgmentWithSignedInfoDto>) => void;
  setSavingInProgress: (value: boolean) => void;
  setBadge: (
    signed: boolean | null,
    statusP1?: GetJudgmentWithSignedInfoDtoStatusP1Enum | null,
  ) => void;
}

const P1Status = GetJudgmentWithSignedInfoDtoStatusP1Enum;

export const SaveAndPrintJudgment: React.FC<BtnProps> = props => {
  const auth = useAuth();
  const szafir = useSzafir();
  const spd = useSpdCore();
  const mountedRef = useRef(false);

  const [savingInProgress, setSavingInProgress] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [messageVariant, setMessageVariant] = useState<string>('');
  const [duplicateArticleConfirm, setDuplicateArticleConfirm] = useState(false);

  const onSave = () => {
    if (!props.judgment.article) {
      setMessage('Nie wybrano podstawy prawnej!');
      setMessageVariant('danger');
      return;
    }
    if (!props.judgment.card && !!props.judgment.article?.cardType) {
      setMessage('Ta podstawa prawna wymaga wybrania karty!');
      setMessageVariant('danger');
      return;
    }
    if (!!props.judgment.card && !props.judgment.article?.cardType) {
      setMessage(
        'Ta podstawa prawna nie powinna być powiązana z kartą. Zmień podstawę lub usuń powiązanie z kartą!',
      );
      setMessageVariant('danger');
      return;
    }
    if (
      !!props.judgment.card &&
      !!props.judgment.article?.cardType &&
      (props.judgment.card.type as unknown as CardType) !==
        (props.judgment.article?.cardType as unknown as CardType)
    ) {
      setMessage(
        'Ta podstawa prawna wymaga karty o typie: ' +
          CardTypeText[props.judgment.article?.cardType],
      );
      setMessageVariant('danger');
      return;
    }
    if (!savingInProgress) {
      if (!props.judgment.id && props.judgment.article?.id) {
        spd.judgments
          .getForExam(props.exam.id)
          .then(existing => {
            const isDuplicate = existing.some(
              j => j.article?.id === props.judgment.article?.id,
            );
            if (isDuplicate) {
              setDuplicateArticleConfirm(true);
            } else {
              doSave();
            }
          })
          .catch(() => doSave());
      } else {
        doSave();
      }
    }
  };

  const doSave = (acknowledgedDuplicate = false) => {
    setMessage('');
    setSavingInProgress(true);
    props.setSavingInProgress(true);

    createJudgment(
      props.judgment?.id,
      {
        ...props.judgment,
        statusP1: null,
        cancelStatusP1: null,
        cancellationReason: null,
      },
      acknowledgedDuplicate,
    ).catch(async response => {
      const errors = await responseErrors(response as ResponseError);
      setMessage(`Błąd podczas zapisywania orzeczenia:\n ${errors}`);
      setMessageVariant('danger');
      setSavingInProgress(false);
    });
  };

  useEffect(() => {
    mountedRef.current = true;
    props.setSavingInProgress(savingInProgress);
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingInProgress]);

  const createJudgment = async (
    id: string | undefined,
    judg: Omit<GetJudgmentWithSignedInfoDto, 'id'>,
    acknowledgedDuplicate = false,
  ) => {
    let pom = id;

    const judgExt = {
      ...judg,
      ...{
        examId: props?.exam?.id,
        articleId: judg.article?.id,
        psychologistUID: judg?.psychologist?.uid,
        cardId: judg?.card?.id,
      },
    };
    const status = await validate(judgExt);
    if (status.valid) {
      let error = false;
      if (id) {
        const newJudg = judgmentData({
          ...judgExt,
          ...{
            id,
          },
        });
        await spd.judgments.update(id, newJudg);
        props.onSave({
          ...judgExt,
          ...{ id },
          statusP1: props.judgment.statusP1,
          cancelStatusP1: props.judgment.cancelStatusP1,
          cancellationReason: props.judgment.cancellationReason,
        });
      } else {
        await spd.judgments
          .create({
            ...judgExt,
            ...{
              psychologist: judg?.psychologist,
              acknowledgedDuplicate,
            },
          } as Parameters<typeof spd.judgments.create>[0])
          .then(async o => {
            props.onSave({ ...o, ...judgExt });
            pom = o.id;
          })
          .catch(async response => {
            const errors = await responseErrors(response as ResponseError);
            setMessage(`Wystąpił problem z zapisem orzeczenia:\n ${errors}`);
            setMessageVariant('danger');
            setSavingInProgress(false);
            error = true;
            return;
          });
      }
      if (!error) {
        props.setBadge(false, props.judgment.withoutSign ? undefined : null);
        const sendToP1 =
          !props.judgment.withoutSign &&
          mustBeSentToP1(judg.article, judg.judgmentDate);
        if (sendToP1 && pom) {
          const lastDoc = await spd.judgmentP1Documents.getLastForJudgment(pom);
          if (String(lastDoc.status) === SimP1StatusEnum.ValidationFailed) {
            props.setBadge(false, P1Status.ValidationFailed);
            setMessage(
              `Orzeczenie zostało zapisane, ale jest niekompletne dla SIM:\n ${lastDoc.error}`,
            );
            setMessageVariant('danger');
            setSavingInProgress(false);
            return;
          }
        }
        if (sendToP1 && auth.canSign && !props.judgment.withoutSign) {
          if (
            pom &&
            !(await signPdfsAndSendToP1(
              pom,
              judg.number || '',
              !!judg.article?.engPdfTemplateType,
            ))
          ) {
            return;
          }
          props.setBadge(null, P1Status.WaitingForP1);
        } else if (!auth.canSign || props.judgment.withoutSign) {
          if (pom) {
            await spd.pdfPrinter.saveAndPrintJudgment(pom);
          }
          setSavingInProgress(false);
          setMessage('Orzeczenie zostało zapisane');
          setMessageVariant('success');
          props.setBadge(false, props.judgment.withoutSign ? undefined : null);
        } else {
          if (pom) {
            await szafirSignAndPrintJudgment(pom);
          }
        }
      }
    } else {
      setMessage(status.errors?.join('\n'));
      setMessageVariant('danger');
      setSavingInProgress(false);
    }
  };

  const signPrintAndSendEmail = async (judgmentId: string, english = false) => {
    try {
      setSavingInProgress(true);
      const taskList = await spd.szafir.getTaskListForJudgment(
        judgmentId,
        english,
      );
      await szafir.signDocument(
        taskList,
        auth.userProfile?.pin || '',
        judgmentId,
        '',
        english,
        saveSignedPdfAndSendEmail,
        signedFailed,
      );
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setMessage(`Błąd pobierania listy 'TaskList':\n ${errors}`);
      setMessageVariant('danger');
      setSavingInProgress(false);
    }
  };

  const saveSignedPdfAndSendEmail = async (
    result: Buffer | null,
    judgmentId: string,
    documentNumber: string,
    english: boolean,
  ) => {
    setSavingInProgress(true);
    await spd.savedPdfs
      .savePdfFileForJudgment({
        entityId: judgmentId,
        file: result,
        english: english,
      })
      .then(() => {
        setMessage(prev => (prev ? prev : 'Orzeczenie zostało podpisane'));
        setMessageVariant('success');
      })
      .then(async () => {
        await spd.emails.sendJudgmentByEmail(judgmentId, english);
      })
      .catch(async response => {
        const errors = await responseErrors(response as ResponseError);
        setMessage(`Błąd:\n ${errors}`);
        setMessageVariant('danger');
      })
      .finally(() => {
        setSavingInProgress(false);
        props.setBadge(true);
      });
  };

  const signedFailed = (error: string) => {
    setMessage(`Błąd podczas podpisywania orzeczenia:\n ${error}`);
    setMessageVariant('danger');
    setSavingInProgress(false);
  };

  const buttonLabel =
    auth.canSign && !props.judgment.withoutSign
      ? mustBeSentToP1(props.judgment.article, props.judgment.judgmentDate)
        ? savingInProgress
          ? 'Wysyłanie'
          : 'Podpisz i Wyślij do SIM'
        : savingInProgress
          ? 'Podpisywanie'
          : 'Podpisz'
      : savingInProgress
        ? 'Zapisywanie'
        : 'Zapisz';

  const szafirSignAndPrintJudgment = async (judgmentId: string) => {
    void spd.pdfPrinter
      .saveWithoutPrintJudgment(judgmentId)
      .then(async () => {
        const judgment = await spd.judgments.get(judgmentId);
        await signPrintAndSendEmail(judgmentId);
        if (judgment.article?.engPdfTemplateType) {
          setSavingInProgress(true);
          await new Promise(f => setTimeout(f, 4000));
          await signPrintAndSendEmail(judgmentId, true);
        }
      })
      .catch(async response => {
        const errors = await responseErrors(response as ResponseError);
        setMessage(`Błąd podczas zapisywania orzeczenia:\n ${errors}`);
        setMessageVariant('danger');
        setSavingInProgress(false);
      });
  };

  const signPdfsAndSendToP1 = async (
    judgmentId: string,
    judgmentNumber: string,
    withEnglish: boolean,
  ): Promise<boolean> => {
    setSavingInProgress(true);
    await spd.pdfPrinter.saveWithoutPrintJudgment(judgmentId);
    if (!(await signPdfForP1(judgmentId, judgmentNumber))) {
      return false;
    }
    if (withEnglish) {
      await new Promise(f => setTimeout(f, 4000));
      if (!(await signPdfForP1(judgmentId, judgmentNumber, true))) {
        return false;
      }
    }
    if (auth.userProfile?.signP1WithSzafir) {
      await new Promise(f => setTimeout(f, 4000));
    }
    return sendToP1AndFinish(judgmentId);
  };

  const signPdfForP1 = (
    judgmentId: string,
    judgmentNumber: string,
    english = false,
  ): Promise<boolean> => {
    return signJudgmentPdfWithSzafir(
      szafir,
      spd,
      auth.userProfile?.pin || '',
      judgmentId,
      judgmentNumber,
      english,
    )
      .then(() => true)
      .catch(async response => {
        const errors = await responseErrors(response as ResponseError);
        setMessage(
          `Błąd podczas podpisywania orzeczenia${english ? ' w wersji angielskiej' : ''}:\n ${errors}`,
        );
        setMessageVariant('danger');
        setSavingInProgress(false);
        return false;
      });
  };

  const sendToP1AndFinish = (judgmentId: string): Promise<boolean> => {
    return sendJudgmentToP1(auth, szafir, spd, judgmentId)
      .then(() => {
        setMessage('Orzeczenie zostało zakolejkowane do wysłania do SIM');
        setMessageVariant('success');
        return true;
      })
      .catch(async response => {
        const errors = await responseErrors(response as ResponseError);
        setMessage(`Błąd wysyłania do SIM:\n ${errors}`);
        setMessageVariant('danger');
        return false;
      })
      .finally(() => {
        setSavingInProgress(false);
      });
  };

  const confirmationMessage = () => {
    let base = 'Orzeczenie zostało już wcześniej podpisane elektronicznie';
    if (
      props.judgment.statusP1 ===
      GetJudgmentWithSignedInfoDtoStatusP1Enum.AcceptedInP1
    ) {
      base += ' i wysłane do SIM.';
    } else {
      base += '.';
    }
    if (!auth.canSign || props.judgment.withoutSign) {
      return `${base} Ponowne zapisanie usunie podpis elektroniczny. Czy chcesz kontynuować?`;
    }
    const p1Note =
      props.judgment.statusP1 ===
      GetJudgmentWithSignedInfoDtoStatusP1Enum.AcceptedInP1
        ? ' Nowa wersja orzeczenia zostanie wysłana do SIM.'
        : '';
    return `${base} Ponowne podpisanie usunie dotychczasowy podpis elektroniczny i wstawi nowy.${p1Note} Czy chcesz kontynuować?`;
  };

  return (
    <>
      {message && <Alert variant={messageVariant || 'danger'}>{message}</Alert>}
      {duplicateArticleConfirm ? (
        <ConfirmationButton
          className="ms-1"
          confirmation="Dla tego badania istnieje już orzeczenie tej samej podstawy prawnej. Czy chcesz mimo to wystawić kolejne?"
          variant="outline-warning"
          autoOpen
          onOK={() => {
            setDuplicateArticleConfirm(false);
            doSave(true);
          }}
          onNoOK={() => setDuplicateArticleConfirm(false)}
          disabled={savingInProgress}
        >
          {buttonLabel}
        </ConfirmationButton>
      ) : props.judgment.signed ? (
        <ConfirmationButton
          className="ms-1"
          confirmation={confirmationMessage()}
          variant="outline-primary"
          onOK={onSave}
          disabled={savingInProgress}
        >
          {buttonLabel}
        </ConfirmationButton>
      ) : (
        <Button
          variant="outline-primary"
          className="ms-1"
          onClick={() => onSave()}
          disabled={savingInProgress}
        >
          {buttonLabel}
        </Button>
      )}
    </>
  );
};
